import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { IGridState, IgxDialogComponent, IgxGridComponent, IgxGridStateDirective, IRowSelectionEventArgs } from '@infragistics/igniteui-angular';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from 'src/app/authentication/user.service';
import { GridState } from 'src/app/models/grid-state';
import { Kid } from 'src/app/models/kid';
import { Package } from 'src/app/models/package';
import { Payment, PaymentForm } from 'src/app/models/payment';
import { PaymentSource } from 'src/app/models/payment-source';
import { AccountingService } from 'src/app/services/accounting.service';
import { KidService } from 'src/app/services/kid.service';
import { MessagesService } from 'src/app/services/messages.service';
import { WaitIndicatorService } from 'src/app/services/wait-indicator.service';
import { restoreState, updateCollection } from 'src/app/shared/utils';

@Component({
    selector: 'app-add-payment',
    templateUrl: './add-payment.component.html',
    styleUrls: ['./add-payment.component.scss']
})
export class AddPaymentComponent implements OnInit, OnDestroy {
    @ViewChild('paymentGrid', { static: true, read: IgxGridComponent })
    private paymentGrid!: IgxGridComponent;
    @ViewChild(IgxGridStateDirective, { static: true })
    private state!: IgxGridStateDirective;

    private destroy$: Subject<void> = new Subject<void>();
    private gridName: string = 'add-payment-grid';
    private gridState?: GridState;

    protected payment?: Payment;
    protected kids: Kid[] = [];
    protected paymentSources: PaymentSource[] = [];
    protected paymentForm: FormGroup<PaymentForm>;
    protected dialogHeader!: string;
    protected dialogText = 'Добави';
    protected currentPackage?: Package;
    protected maxDate = new Date();

    @Input() public kid?: Kid;
    @Input() public packages: Package[] = [];

    constructor(
        private kidService: KidService,
        private accountService: AccountingService,
        private msgs: MessagesService,
        private waitIndicatorService: WaitIndicatorService,
        private userService: UserService,
        router: Router
    ) {
        this.paymentForm = new FormGroup<PaymentForm>(this.emptyPaymentForm());
        this.maxDate.setDate(this.maxDate.getDate() + 7);
        this.load();
        router.events.pipe(takeUntil(this.destroy$)).subscribe(e => {
            if (e instanceof NavigationStart) {
                if (this.gridState) {
                    this.gridState.options = this.state.getState().toString();
                    this.gridState.userId = this.userService.currentUser!.id!;
                } else {
                    this.gridState = {
                        gridName: this.gridName,
                        options: this.state.getState().toString(),
                        userId: this.userService.currentUser!.id!,
                    };
                }
                this.userService.updateGridState(this.gridState);
            }
        });
    }

    ngOnInit(): void {
        this.userService.gridStates().pipe(takeUntil(this.destroy$)).subscribe((states: GridState[]) => {
            const loaded = states.filter(s => s.gridName === this.gridName && s.userId === this.userService.currentUser!.id!)[0];
            if (loaded) {
                this.gridState = loaded;
                const gridState: IGridState = JSON.parse(this.gridState?.options ?? '');
                restoreState(gridState, this.paymentGrid);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected kidSelected = async (e: IRowSelectionEventArgs, dialog: IgxDialogComponent): Promise<void> => {
        this.waitIndicatorService.show();
        this.kid = await this.kidService.kidInfo(e.newSelection[0].id.replaceAll('-', ''));
        this.waitIndicatorService.hide();
        this.packages = this.kid.packages;
        this.dialogHeader = `Справка за ${this.kid.name}`
        dialog.open();
    }

    protected paid = (p: Package): number => {
        return p.payments.map(p => p.amount).reduce((a, b) => a + b, 0);
    }

    protected toPay(p: Package): number {
        return p.price - this.paid(p);
    }

    protected addPayment = (p: Package, dialog: IgxDialogComponent): void => {
        this.currentPackage = p;
        this.paymentForm = new FormGroup<PaymentForm>(this.emptyPaymentForm());
        dialog.open();
    }

    protected editPayment = (p: Package, dialog: IgxDialogComponent): void => {
        this.currentPackage = p;
        this.paymentForm = new FormGroup<PaymentForm>(this.emptyPaymentForm());
        dialog.open();
    }

    protected updatePayment = (p: Payment, dialog: IgxDialogComponent): void => {
        this.payment = p;
        this.paymentForm.reset({
            amount: this.payment.amount,
            date: this.payment.date,
            source: this.payment.source?.id,
        });
        this.dialogText = 'Редактирай';
        dialog.open();
    }

    protected deletePayment = (p: Payment, dialog: IgxDialogComponent): void => {
        this.payment = p;
        dialog.open();
    }

    protected doDeletePayment = async (dialog: IgxDialogComponent): Promise<void> => {
        const amount = this.payment!.amount;
        try {
            this.waitIndicatorService.show();
            const result = await this.accountService.deletePayment(this.payment?.id);
            if (result === 'true') {
                let index = this.currentPackage?.payments.findIndex(p => p.id?.replaceAll('-', '') === this.payment?.id?.replaceAll('-', '')) ?? -1;
                if (index > -1) {
                    this.currentPackage?.payments.splice(index, 1);
                }
                this.currentPackage!.payments = [...this.currentPackage?.payments ?? []];
                this.payment = undefined;

                this.kid!.balance! += amount
                index = this.kids.findIndex(k => k.id === this.kid?.id);
                if (index > -1) {
                    this.kids.splice(index, 1, this.kid!);
                    this.kids = [...this.kids];
                }
                this.msgs.success('Плащането е изтрито!');
            } else {
                this.msgs.error('Плащането не е изтрито!');
            }
        } catch (error) {
            this.msgs.error('Плащането не е изтрито!');
        } finally {
            dialog.close();
            this.waitIndicatorService.hide();
        }
    }

    protected paymentSubmit = async (dialog: IgxDialogComponent): Promise<void> => {
        this.waitIndicatorService.show();
        let initialAmount = 0;
        if (this.payment) {
            initialAmount = this.payment.amount;
        }
        const data: any = this.paymentForm.value;
        data.user = this.userService.currentUser?.id?.replaceAll('-', '');
        data.package = this.currentPackage?.id?.replaceAll('-', '');
        try {
            if (this.dialogText === 'Добави') {
                this.payment = await this.accountService.addPayment(data);
            } else {
                data.id = this.payment?.id?.replaceAll('-', '');
                this.payment = await this.accountService.updatePayment(data);
            }
            this.kid!.balance! -= this.payment!.amount - initialAmount;
            this.msgs.success(`Плащане в размер на ${this.payment!.amount} е успешно записано!`);
        } catch (error) {
            this.msgs.error('Плащането не е запазено!');
        } finally {
            this.kids = updateCollection(this.kid, this.kids);
            this.currentPackage!.payments = updateCollection(this.payment, this.currentPackage?.payments ?? []);
            dialog.close();
            this.waitIndicatorService.hide();
            this.paymentForm = new FormGroup<PaymentForm>(this.emptyPaymentForm());
            this.payment = undefined;
            this.dialogText = 'Добави';
        }
    }

    private load = async (): Promise<void> => {
        this.waitIndicatorService.show();
        this.kids = await this.kidService.allKids();
        this.paymentSources = await this.accountService.allSources();
        this.dialogHeader = 'Не е избрано дете!';
        this.waitIndicatorService.hide();
    }

    private emptyPaymentForm(): PaymentForm {
        return {
            amount: new FormControl(0, [Validators.required, Validators.min(1)]),
            date: new FormControl(new Date(), Validators.required),
            source: new FormControl(null, Validators.required),
        }
    }
}
